import React, { useState } from 'react';

interface SchedulePostProps {
  isOpen: boolean;
  onClose: () => void;
  onSchedule: (date: Date) => void;
}

const SchedulePost: React.FC<SchedulePostProps> = ({ isOpen, onClose, onSchedule }) => {
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');

  const handleSchedule = () => {
    if (selectedDate && selectedTime) {
      const scheduledDate = new Date(`${selectedDate}T${selectedTime}`);
      
      // Vérifier que la date est dans le futur
      if (scheduledDate > new Date()) {
        onSchedule(scheduledDate);
        onClose();
      } else {
        alert('Veuillez sélectionner une date dans le futur');
      }
    }
  };

  const getMinDate = () => {
    const now = new Date();
    return now.toISOString().split('T')[0];
  };

  const getMinTime = () => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    
    if (selectedDate === today) {
      // Si c'est aujourd'hui, minimum = heure actuelle + 5 minutes
      const minTime = new Date(now.getTime() + 5 * 60000);
      return minTime.toTimeString().slice(0, 5);
    }
    return '00:00';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="cosmic-card max-w-md w-full">
        <div className="flex items-center justify-between p-4 border-b border-emerald-800/30">
          <h3 className="text-lg font-semibold text-emerald-100">Programmer la publication</h3>
          <button
            onClick={onClose}
            className="text-emerald-300/80 hover:text-emerald-100 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex items-center space-x-2 text-emerald-300 mb-4">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm">Choisissez quand publier votre post</span>
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-emerald-200 mb-2">
              Date
            </label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              min={getMinDate()}
              className="w-full bg-gray-800/70 border border-emerald-800/50 rounded-lg px-3 py-2 text-emerald-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/70"
            />
          </div>

          {/* Heure */}
          <div>
            <label className="block text-sm font-medium text-emerald-200 mb-2">
              Heure
            </label>
            <input
              type="time"
              value={selectedTime}
              onChange={(e) => setSelectedTime(e.target.value)}
              min={getMinTime()}
              className="w-full bg-gray-800/70 border border-emerald-800/50 rounded-lg px-3 py-2 text-emerald-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/70"
            />
          </div>

          {/* Aperçu */}
          {selectedDate && selectedTime && (
            <div className="bg-emerald-900/20 border border-emerald-700/30 rounded-lg p-3">
              <div className="text-sm text-emerald-200">
                <span className="font-medium">Publication programmée :</span>
                <br />
                {new Date(`${selectedDate}T${selectedTime}`).toLocaleString('fr-FR', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            </div>
          )}

          {/* Suggestions rapides */}
          <div>
            <div className="text-sm text-emerald-200 mb-2">Suggestions rapides :</div>
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: 'Dans 1h', minutes: 60 },
                { label: 'Dans 3h', minutes: 180 },
                { label: 'Demain 9h', minutes: null, specific: 'tomorrow-9am' },
                { label: 'Demain 18h', minutes: null, specific: 'tomorrow-6pm' }
              ].map((suggestion) => (
                <button
                  key={suggestion.label}
                  onClick={() => {
                    const now = new Date();
                    let targetDate: Date;
                    
                    if (suggestion.minutes) {
                      targetDate = new Date(now.getTime() + suggestion.minutes * 60000);
                    } else if (suggestion.specific === 'tomorrow-9am') {
                      targetDate = new Date(now);
                      targetDate.setDate(targetDate.getDate() + 1);
                      targetDate.setHours(9, 0, 0, 0);
                    } else if (suggestion.specific === 'tomorrow-6pm') {
                      targetDate = new Date(now);
                      targetDate.setDate(targetDate.getDate() + 1);
                      targetDate.setHours(18, 0, 0, 0);
                    } else {
                      return;
                    }
                    
                    setSelectedDate(targetDate.toISOString().split('T')[0]);
                    setSelectedTime(targetDate.toTimeString().slice(0, 5));
                  }}
                  className="text-xs px-3 py-2 bg-emerald-800/30 hover:bg-emerald-700/30 text-emerald-200 rounded-lg transition-colors"
                >
                  {suggestion.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3 p-4 border-t border-emerald-800/30">
          <button
            onClick={onClose}
            className="px-4 py-2 text-emerald-300 hover:text-emerald-100 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleSchedule}
            disabled={!selectedDate || !selectedTime}
            className="cosmic-button disabled:opacity-50"
          >
            Programmer
          </button>
        </div>
      </div>
    </div>
  );
};

export default SchedulePost;
